import React, { useContext } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';

import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from 'src/presentation/components/pages/ivr/components/NodeSelector';
import {
  Content,
  Footer,
  Row,
  ScrollRow,
  StyledTextarea,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  DESCRIPTION_LABEL_TEXT,
  DESCRIPTION_PLACEHOLDER_TEXT,
  HTTP_METHODS_OPTIONS,
  NAME_PLACEHOLDER_TEXT,
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import Http2NodeHeaders from './components/Http2NodeHeaders';
import HttpNodeVariables from './components/HttpNodeVariables';
import { NanoSecondsInputField } from 'src/presentation/components/pages/shared/NanoSecondsInputField';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';

const Http2Node = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }: NodeProps) => {
  const isHttp2NodeEditEnabled = useIsFeatureEnabled()('enableEditingHttp2Node');

  const { workingState } = useContext(FormContext);
  const currentNodeId = get(workingState, `${attr}.id`);
  const isPostHttpMethod = get(workingState, `${attr}.request.method`) === 'POST';

  const isDisabled = !isHttp2NodeEditEnabled || disabled;

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid="name"
            disabled={disabled}
            label="Name"
            description="The name you can refer to link to other nodes"
            readOnly={readOnly}
            placeholder={NAME_PLACEHOLDER_TEXT}
          />
        </Row>
        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid="description"
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>
        <Row>
          <InputField
            attr={`${attr}.request.urlTemplate`}
            data-aid="request.urlTemplate"
            description="Enter the URL that specifies the endpoint where the request should be sent"
            disabled={isDisabled}
            label="HTTP Request"
            readOnly={readOnly}
          />
        </Row>
        <Row>
          <Dropdown
            attr={`${attr}.request.method`}
            data-aid="request.method"
            description="The desired HTTP method of your request"
            disabled={isDisabled}
            label="Method"
            options={HTTP_METHODS_OPTIONS}
            readOnly={readOnly}
          />
        </Row>
        <Row>
          <NanoSecondsInputField
            attr={`${attr}.timeout`}
            data-aid={`${attr}.timeout`}
            description="The amount of time allocated before request cancelation."
            disabled={disabled}
            label="Timeout"
            readOnly={readOnly}
          />
        </Row>

        {isPostHttpMethod && (
          <>
            <Row>
              <InputField
                attr={`${attr}.request.contentType`}
                data-aid="request.contentType"
                description="The HTTP Content-Type of the body; required when method is POST"
                disabled={isDisabled}
                label="Content Type"
                readOnly={readOnly}
              />
            </Row>
            <Row>
              <StyledTextarea
                attr={`${attr}.request.bodyTemplate`}
                data-aid="request.bodyTemplate"
                description="The HTTP request body; required when method is POST"
                disabled={isDisabled}
                label="Body"
                readOnly={readOnly}
              />
            </Row>
          </>
        )}
        <Content>
          <Http2NodeHeaders attr={`${attr}.request`} disabled={isDisabled} readOnly={readOnly} />
        </Content>

        <HttpNodeVariables attr={attr} disabled={disabled} readOnly={!!readOnly} />

        <Row>
          <InputField
            attr={`${attr}.credentialId`}
            data-aid="credentialId"
            disabled={isDisabled}
            label="Credential ID"
            readOnly={readOnly}
          />
        </Row>
      </Content>

      <Footer>
        <Row>
          <NodeSelector
            searchable
            attr={`${attr}.nextId`}
            data-aid="nextId"
            description="The next node in the IVR"
            disabled={disabled}
            label="Next Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </Row>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.ERROR_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.errorNodeId`}
            data-aid="errorNodeId"
            description="When an error occurs, go to this node"
            disabled={disabled}
            label="Error Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export const StyledLink = styled.a`
  display: inline-flex;
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }

  & > img {
    padding-left: 4px;
  }
`;

export default Http2Node;
